import React from 'react';

/* ====================================================================
 * <ScientificFrameworks /> — editorial framework section on tagc.us
 *
 * Lists the scientific and regulatory frameworks Aᴳ engages with in its
 * day-to-day evaluation work. Editorial cards, not bullet vomit — each
 * framework gets a clear what/where/how-it-applies.
 *
 * Three cinematic banners woven through the section narrative:
 *   • Under the headline — "the work in motion" (annotation)
 *   • Between Regulatory ↔ Scientific groups — "the archive"
 *   • Above the signature footer — "the moment of accountability"
 *
 * Pure presentational. No data fetching, no auth.
 * ==================================================================== */

const SLATE = '#0B1426';
const MUTED = '#5B6577';
const CORAL = '#FF4E6A';

const FRAMEWORKS = [
  {
    group: 'Regulatory',
    items: [
      {
        name: 'MoCRA',
        full: 'Modernization of Cosmetics Regulation Act',
        scope: 'U.S. cosmetic facility & product registration, adverse-event reporting, safety-substantiation evidence requirements.',
      },
      {
        name: 'EU Cosmetics Regulation 1223/2009',
        full: 'Regulation (EC) No 1223/2009',
        scope: 'Cosmetic Product Safety Reports (CPSR), responsible-person obligations, Annex II/III/IV/V/VI ingredient controls.',
      },
      {
        name: 'California Proposition 65',
        full: 'Safe Drinking Water and Toxic Enforcement Act',
        scope: 'Exposure-based assessment vs Safe Harbor Levels; warning-decision logic; NSRL / MADL framing.',
      },
      {
        name: 'REACH',
        full: 'Regulation (EC) No 1907/2006',
        scope: 'EU registration, evaluation, authorisation, and restriction of chemicals — hazard, exposure, and risk characterisation for substances placed on the EU market.',
      },
      {
        name: 'CLP',
        full: 'Regulation (EC) No 1272/2008',
        scope: 'Classification, labelling, and packaging of substances and mixtures — hazard-class assignment, pictogram selection, and label composition per EU CLP.',
      },
      {
        name: 'OSHA Hazard Communication · GHS',
        full: 'OSHA HCS · UN GHS Purple Book',
        scope: 'Hazard-communication classification, SDS authoring, and global-market label harmonisation via the Globally Harmonized System.',
      },
      {
        name: 'ISO 10993',
        full: 'Biological evaluation of medical devices',
        scope: 'Toxicological risk assessment, biological evaluation planning, and chemical-characterisation review — scientific support only, not certification.',
      },
      {
        name: 'ICH M7',
        full: 'Mutagenic impurities in pharmaceuticals',
        scope: 'Nitrosamine and DNA-reactive impurity assessment, TTC-based control strategies, and PDE-derivation support for drug substances and products.',
      },
      {
        name: 'SCCS Opinions',
        full: 'EU Scientific Committee on Consumer Safety',
        scope: 'Authoritative EU opinions on cosmetic ingredients; SCCS Notes of Guidance methodology baseline.',
      },
      {
        name: 'IFRA Standards',
        full: 'International Fragrance Association',
        scope: 'Fragrance-ingredient use restrictions, dermal sensitization QRA, category-specific limits.',
      },
    ],
  },
  {
    group: 'Scientific',
    items: [
      {
        name: 'Exposure-based risk assessment',
        full: 'SED · MoS · BMD methodology',
        scope: 'Quantitative dermal, oral, inhalation, and mucosal exposure modelling per population band.',
      },
      {
        name: 'Margin of Safety',
        full: 'NOAEL / LOAEL → MoS',
        scope: 'Internal-dose modelling against point-of-departure values; uncertainty factor selection and justification.',
      },
      {
        name: 'Ingredient hazard review',
        full: 'Hazard characterization',
        scope: 'Acute, subchronic, and chronic endpoint review across genotoxicity, sensitization, irritation, repro/dev, and systemic toxicity.',
      },
      {
        name: 'Weight-of-evidence review',
        full: 'WoE evaluation',
        scope: 'Structured integration of human, animal, in-vitro, and in-silico data streams with study-quality weighting.',
      },
      {
        name: 'Scientific substantiation',
        full: 'Claim & label substantiation',
        scope: 'Defensible documentation of safety, performance, and regulatory claims for retailer and litigation contexts.',
      },
    ],
  },
];

function Banner({ src, alt, testId, height = 'h-[32vh] min-h-[260px] max-h-[440px]' }) {
  return (
    <div
      className={`relative w-full overflow-hidden ${height}`}
      data-testid={testId}
      aria-hidden="true"
    >
      <img
        src={src}
        alt={alt || ''}
        className="block w-full h-full object-cover select-none"
        loading="lazy"
      />
    </div>
  );
}

function GroupGrid({ group }) {
  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10">
      <p
        className="text-[10px] font-mono uppercase tracking-[0.3em] mb-8 pb-3 border-b"
        style={{ color: SLATE, borderColor: '#D6CDB8' }}
        data-testid={`frameworks-group-${group.group.toLowerCase()}`}
      >
        {group.group} frameworks
      </p>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10">
        {group.items.map((f) => (
          <article
            key={f.name}
            data-testid={`framework-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
          >
            <p
              className="text-[10px] font-mono uppercase tracking-[0.22em] mb-2"
              style={{ color: CORAL }}
            >
              {f.full}
            </p>
            <h3
              className="font-serif text-xl mb-3 leading-tight"
              style={{ color: SLATE }}
            >
              {f.name}
            </h3>
            <p
              className="text-[13.5px] leading-relaxed"
              style={{ color: MUTED }}
            >
              {f.scope}
            </p>
          </article>
        ))}
      </div>
    </div>
  );
}

export default function ScientificFrameworks() {
  const regulatory = FRAMEWORKS[0];
  const scientific = FRAMEWORKS[1];

  return (
    <section
      id="frameworks"
      className="border-t"
      style={{ borderColor: '#E3D9C0', backgroundColor: '#FBF7EE' }}
      data-testid="tagc-frameworks"
    >
      {/* Section header */}
      <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-24 pb-12">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p
              className="text-[11px] font-mono uppercase tracking-[0.3em] mb-4"
              style={{ color: CORAL }}
              data-testid="frameworks-eyebrow"
            >
              Frameworks &amp; methodology
            </p>
            <h2
              className="font-serif text-4xl lg:text-5xl leading-[1.05]"
              style={{ color: SLATE }}
            >
              The science we apply.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 lg:pt-3">
            <p className="text-base lg:text-lg leading-relaxed" style={{ color: MUTED }}>
              Every Aᴳ evaluation references the same regulatory frameworks and
              scientific methodologies a competent authority, retailer, or court
              would reference. We are explicit about which framework applies, where
              its boundary lies, and what evidence the engagement produces.
            </p>
          </div>
        </div>
      </div>

      {/* Banner A — annotation, the work in motion */}
      <Banner
        src="/tagc/generated/v2_frameworks_annotating.jpg"
        alt="Hands annotating a printed regulatory document with a fountain pen."
        testId="frameworks-banner-annotating"
      />

      {/* Regulatory frameworks */}
      <div className="pt-16 pb-12">
        <GroupGrid group={regulatory} />
      </div>

      {/* Banner B — archive, the documents themselves */}
      <Banner
        src="/tagc/generated/v2_frameworks_documents_flatlay.jpg"
        alt="Overhead flat-lay of regulatory documents on a cream-paper desk."
        testId="frameworks-banner-flatlay"
      />

      {/* Scientific frameworks */}
      <div className="pt-16 pb-12">
        <GroupGrid group={scientific} />
      </div>

      {/* Banner D — signature, the moment of accountability */}
      <Banner
        src="/tagc/generated/v2_frameworks_signature.jpg"
        alt="A dark fountain pen completing a signature on a thick dossier cover."
        testId="frameworks-banner-signature"
      />

      {/* Footer line — the signature line, deliberately placed after the signature banner */}
      <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-8 pb-24">
        <p
          className="text-[12px] font-mono uppercase tracking-[0.22em] pt-6 border-t"
          style={{ color: MUTED, borderColor: '#D6CDB8' }}
          data-testid="frameworks-footer"
        >
          All assessments are signed by a named toxicologist &middot; documented for
          regulatory, retailer, and litigation contexts.
        </p>
      </div>
    </section>
  );
}
